import { useEffect } from 'react';
import { canHaveOwnershipShare } from '../../../utils/isGeneralUser';
import {
  formatOwnershipShareSum,
  isOwnershipShareSumComplete,
  parseOwnershipShareInput,
} from '../../../utils/ownershipShare';
import { useAircraft } from '../../../aircraft/AircraftContext';
import { useAuth } from '../../../hooks/useAuth';
import { useFeedback } from '../../../hooks/useFeedback';
import { assignableRolesFor } from '../membersUtils';
import { useMemberInvite } from './useMemberInvite';
import { useMemberLifecycle } from './useMemberLifecycle';
import { useMemberRoles } from './useMemberRoles';
import { useMembersData } from './useMembersData';
import { useOwnershipShares } from './useOwnershipShares';

export type { AddTab } from './useMemberLifecycle';

export function useMembersPanel() {
  const { currentUserId, user, isAdmin, patchUser } = useAuth();
  const { aircraftId, aircraftList } = useAircraft();
  const actorClubRole = user?.club_role;
  const { showError, showSuccess, feedbackNode } = useFeedback();
  const roleOptions = assignableRolesFor(actorClubRole);
  const createRoleOptions = roleOptions.filter((r) => r.value !== 'owner');

  const data = useMembersData({
    isAdmin,
    aircraftId,
    aircraftList,
    showError,
  });
  const invite = useMemberInvite({ showError, showSuccess });
  const shares = useOwnershipShares({
    members: data.members,
    loadMembers: data.loadMembers,
    sharesAircraftId: data.sharesAircraftId,
    setSharesAircraftId: data.setSharesAircraftId,
    currentUserId,
    showError,
    showSuccess,
    patchUser,
  });
  const lifecycle = useMemberLifecycle({
    members: data.members,
    loadMembers: data.loadMembers,
    openSharesModal: shares.openSharesModal,
    setInviteLink: invite.setInviteLink,
    setJoinedDrafts: data.setJoinedDrafts,
    showError,
    showSuccess,
  });
  const roles = useMemberRoles({
    members: data.members,
    loadMembers: data.loadMembers,
    openSharesModal: shares.openSharesModal,
    showError,
    showSuccess,
    patchUser,
    setBusyId: lifecycle.setBusyId,
  });

  const { inviteRole, setInviteRole } = invite;
  const { form, setForm } = lifecycle;

  useEffect(() => {
    if (actorClubRole === 'owner') return;
    if (inviteRole === 'admin' || inviteRole === 'moderator') {
      setInviteRole('pilot');
    }
    if (form.role === 'admin' || form.role === 'moderator') {
      setForm((prev) => ({ ...prev, role: 'pilot' }));
    }
  }, [actorClubRole, inviteRole, form.role, setInviteRole, setForm]);

  const owners = data.members.filter((m) =>
    canHaveOwnershipShare(m.role, m.is_pilot)
  );
  const pendingMembers = data.members.filter((m) => m.role === 'pending');
  const others = data.members.filter(
    (m) =>
      !canHaveOwnershipShare(m.role, m.is_pilot) && m.role !== 'pending'
  );
  const modalMembers = data.members.filter((m) =>
    shares.sharesModalMemberIds.includes(m.id)
  );
  const modalShareSum = modalMembers.reduce((acc, m) => {
    const parsed = parseOwnershipShareInput(shares.shareDrafts[m.id] ?? '');
    if (parsed === 'invalid' || parsed == null) return acc;
    return acc + parsed;
  }, 0);
  const modalShareSumLabel = formatOwnershipShareSum(modalShareSum);
  const modalShareSumOk = isOwnershipShareSumComplete(modalShareSum);

  return {
    currentUserId,
    user,
    isAdmin,
    actorClubRole,
    feedbackNode,
    sharesAircraftId: data.sharesAircraftId,
    setSharesAircraftId: data.setSharesAircraftId,
    members: data.members,
    loading: data.loading,
    addOpen: lifecycle.addOpen,
    setAddOpen: lifecycle.setAddOpen,
    addTab: lifecycle.addTab,
    setAddTab: lifecycle.setAddTab,
    inviteLink: invite.inviteLink,
    setInviteLink: invite.setInviteLink,
    inviteRole: invite.inviteRole,
    setInviteRole: invite.setInviteRole,
    form: lifecycle.form,
    setForm: lifecycle.setForm,
    deactivating: lifecycle.deactivating,
    setDeactivating: lifecycle.setDeactivating,
    transferring: roles.transferring,
    setTransferring: roles.setTransferring,
    restoring: lifecycle.restoring,
    setRestoring: lifecycle.setRestoring,
    restoreRole: lifecycle.restoreRole,
    setRestoreRole: lifecycle.setRestoreRole,
    busyId: lifecycle.busyId,
    adding: lifecycle.adding,
    joinedDrafts: data.joinedDrafts,
    setJoinedDrafts: data.setJoinedDrafts,
    sharesModalOpen: shares.sharesModalOpen,
    sharesModalMemberIds: shares.sharesModalMemberIds,
    setSharesModalMemberIds: shares.setSharesModalMemberIds,
    sharesLeaveUserId: shares.sharesLeaveUserId,
    sharesRestoreRole: shares.sharesRestoreRole,
    sharesRoleChange: shares.sharesRoleChange,
    sharesOwnerIsPilot: shares.sharesOwnerIsPilot,
    sharesAircraftLocked: shares.sharesAircraftLocked,
    sharesEffectiveFrom: shares.sharesEffectiveFrom,
    setSharesEffectiveFrom: shares.setSharesEffectiveFrom,
    shareDrafts: shares.shareDrafts,
    setShareDrafts: shares.setShareDrafts,
    shareDraftsByAircraft: shares.shareDraftsByAircraft,
    setShareDraftsByAircraft: shares.setShareDraftsByAircraft,
    sharesSaving: shares.sharesSaving,
    shareHistory: shares.shareHistory,
    historyLoading: shares.historyLoading,
    periodToDelete: shares.periodToDelete,
    setPeriodToDelete: shares.setPeriodToDelete,
    roleOptions,
    createRoleOptions,
    closeAddModal: lifecycle.closeAddModal,
    handleInvite: invite.handleInvite,
    handleAdd: lifecycle.handleAdd,
    handleDeactivateConfirm: lifecycle.handleDeactivateConfirm,
    handleActivate: lifecycle.handleActivate,
    handleRestoreConfirm: lifecycle.handleRestoreConfirm,
    handleRoleChange: roles.handleRoleChange,
    confirmTransferOwnership: roles.confirmTransferOwnership,
    commitJoinedOn: lifecycle.commitJoinedOn,
    openSharesModal: shares.openSharesModal,
    openSharesForAircraft: shares.openSharesForAircraft,
    closeSharesModal: shares.closeSharesModal,
    saveOwnershipShares: shares.saveOwnershipShares,
    confirmDeleteSharePeriod: shares.confirmDeleteSharePeriod,
    copyInviteLink: invite.copyInviteLink,
    owners,
    pendingMembers,
    others,
    modalMembers,
    modalShareSumLabel,
    modalShareSumOk,
    aircraftList,
  };
}

export type MembersPanelModel = ReturnType<typeof useMembersPanel>;
